// Port of TrailMap/SwisstopoMapProvider.swift (TrailBivvyKit): official swisstopo / geo.admin.ch WMTS in EPSG:3857,
// `https://wmts.geo.admin.ch/1.0.0/{layer}/default/current/3857/{z}/{x}/{y}.{ext}` (XYZ scheme, 256 px tiles).
//
// Layer ids, formats and native maximum zooms were checked by the iOS project against the 3857 WMTS GetCapabilities
// (2026-09-27); CORS was checked for the web on 2026-10-20 (Access-Control-Allow-Origin: *). Data are swisstopo / FOEN
// open government data ("© swisstopo", "© BAFU/FOEN"); bulk offline pre-fetching under the FSDI fair-use terms is a
// grey zone pending human review (src/map/PROVIDERS.md).
//
// Coverage: every layer carries Switzerland + Liechtenstein (`switzerlandBounds` refined by the bundled outline), so
// offline packages of cross-border treks only contain swisstopo tiles on the Swiss side (plus `coverageMarginMeters`).
//
// `protectedAreas(bounds)` returns the injected local (bundled/offline) areas, merged with the official wildlife rest
// zones from the geo.admin.ch REST `identify` service when a client is provided and the device is online. Network
// failures (and an exhausted REST fair-use budget) never surface: the local areas are returned instead.

import type { EpochMs, GeoBounds, OfflinePackageLevel, OfflineRegion, ProtectedArea, TrailRoute } from '../core/models';
import { boundsIntersect, switzerlandBounds } from '../core/geo';
import type { HTTPClient } from '../net/httpTypes';
import type { ConnectivityProviding } from '../net/connectivity';
import { CoverageArea } from '../offline/CoverageArea';
import { makeTileLayerDescriptor, type TileLayerDescriptor } from '../offline/TileLayerDescriptor';
import { FairUseLimiter, GeoAdminWildlifeRestZones, geoAdminRESTLimiter } from './GeoAdminWildlifeRestZones';
import { isMapLayerEnabled, type MapLayerPreferences } from './MapLayer';
import { ProviderCoverage } from './ProviderCoverage';
import { RouteRegionSplitter } from './RouteRegionSplitter';
import { corsEnabledFor } from './tileHosts';
import {
  MapAttributions,
  noLocalProtectedAreas,
  type LocalProtectedAreas,
  type MapAttribution,
  type MapStyle,
  type TrailMapProvider,
} from './TrailMapProvider';

const wmtsBase = 'https://wmts.geo.admin.ch/1.0.0';

function template(layerID: string, fileExtension: string): string {
  return `${wmtsBase}/${layerID}/default/current/3857/{z}/{x}/{y}.${fileExtension}`;
}

/** A swisstopo/FOEN layer limited to the Swiss coverage. */
function layer(
  layerID: string,
  fileExtension: string,
  maxZoom: number,
  averageTileBytes: number,
  attribution: string,
  isOpaque: boolean,
): TileLayerDescriptor {
  const urlTemplate = template(layerID, fileExtension);
  return makeTileLayerDescriptor({
    id: layerID,
    urlTemplate,
    fileExtension,
    minZoom: 0,
    maxZoom,
    averageTileBytes,
    attribution,
    isOpaque,
    allowsOfflineStorage: true,
    corsEnabled: corsEnabledFor(urlTemplate),
    coverage: switzerlandBounds,
    coveragePolygons: ProviderCoverage.polygons('CH'),
  });
}

export interface SwisstopoMapProviderOptions {
  /** When provided, wildlife rest zones are also fetched from geo.admin.ch while online. */
  client?: HTTPClient;
  /** Areas from the bundled/offline datasets for the given bounds. */
  localProtectedAreas?: LocalProtectedAreas;
  /** When provided and offline, no request is attempted. */
  connectivity?: ConnectivityProviding;
  /** Clock stamped into the provenance of fetched areas. */
  now?: () => EpochMs;
  /** REST fair-use budget (default: the shared `geoAdminRESTLimiter`). */
  limiter?: FairUseLimiter;
}

let swissArea: CoverageArea | undefined;

export class SwisstopoMapProvider implements TrailMapProvider {
  readonly id = 'swisstopo';
  readonly displayName = 'swisstopo';
  readonly coverage: GeoBounds = switzerlandBounds;

  /**
   * Remote queries are skipped for bounds larger than this span (degrees): zoomed-out views would need many pages and
   * exceed the REST fair-use budget. Local areas are still returned.
   */
  static readonly maximumRemoteQuerySpanDegrees = 1.0;

  // MARK: Layers

  /** National map, colour (Landeskarte). JPEG, native z0–19; ~32 KB per tile in Alpine terrain. */
  static readonly topographic = layer('ch.swisstopo.pixelkarte-farbe', 'jpeg', 19, 32_000, '© swisstopo', true);
  /** SWISSIMAGE aerial imagery. JPEG, native z0–20. Not part of offline packages (size). */
  static readonly aerial = layer('ch.swisstopo.swissimage', 'jpeg', 20, 40_000, '© swisstopo, CNES, Spot Image, NPOC', true);
  /** Official hiking trail network (swissTLM3D). Transparent PNG, native z0–18; mostly sparse. */
  static readonly hikingTrails = layer('ch.swisstopo.swisstlm3d-wanderwege', 'png', 18, 6_000, '© swisstopo', false);
  /** Slope classes over 30°. Transparent PNG, native z0–17. */
  static readonly slope30 = layer('ch.swisstopo.hangneigung-ueber_30', 'png', 17, 8_000, '© swisstopo', false);
  /** swissALTI3D multidirectional hillshade. PNG, native z0–18; heavy, online only. */
  static readonly relief = layer('ch.swisstopo.swissalti3d-reliefschattierung', 'png', 18, 40_000, '© swisstopo', false);
  /** Wildlife rest zones (cantons + FOEN). Transparent PNG, native z0–18. */
  static readonly wildlifeRestZones = layer('ch.bafu.wrz-wildruhezonen_portal', 'png', 18, 3_000, '© BAFU/FOEN, cantons', false);
  /** Federal hunting ban areas / wildlife reserves. Transparent PNG, native z0–18. */
  static readonly wildlifeReserves = layer('ch.bafu.bundesinventare-jagdbanngebiete', 'png', 18, 4_000, '© BAFU/FOEN', false);

  /** All layers of this provider. */
  static readonly allLayers: readonly TileLayerDescriptor[] = [
    SwisstopoMapProvider.topographic,
    SwisstopoMapProvider.aerial,
    SwisstopoMapProvider.hikingTrails,
    SwisstopoMapProvider.slope30,
    SwisstopoMapProvider.relief,
    SwisstopoMapProvider.wildlifeRestZones,
    SwisstopoMapProvider.wildlifeReserves,
  ];

  /** The provider's area: `switzerlandBounds` refined by the bundled outline of Switzerland and Liechtenstein. */
  static get coverageArea(): CoverageArea {
    return swissArea ?? (swissArea = new CoverageArea(switzerlandBounds, ProviderCoverage.polygons('CH')));
  }

  private readonly client?: HTTPClient;
  private readonly connectivity?: ConnectivityProviding;
  private readonly localProtectedAreas: LocalProtectedAreas;
  private readonly now: () => EpochMs;
  private readonly limiter: FairUseLimiter;

  constructor(options: SwisstopoMapProviderOptions = {}) {
    this.client = options.client;
    this.connectivity = options.connectivity;
    this.localProtectedAreas = options.localProtectedAreas ?? noLocalProtectedAreas;
    this.now = options.now ?? (() => Date.now());
    this.limiter = options.limiter ?? geoAdminRESTLimiter;
  }

  // MARK: TrailMapProvider

  /**
   * Base: topographic or aerial. Overlays (bottom → top): relief (`elevation`), slope > 30° (`slope`), hiking trails
   * (`hikingRoutes`), FOEN wildlife rest zones + reserves (`wildlifeZones`). Attributions: the credit of every
   * displayed layer as its licence requires, de-duplicated (a credit contained in one already shown is skipped).
   */
  mapStyle(_bounds: GeoBounds, preferences: MapLayerPreferences): MapStyle {
    const base = preferences.baseLayer === 'satellite' ? SwisstopoMapProvider.aerial : SwisstopoMapProvider.topographic;
    const overlays: TileLayerDescriptor[] = [];
    if (isMapLayerEnabled(preferences, 'elevation')) overlays.push(SwisstopoMapProvider.relief);
    if (isMapLayerEnabled(preferences, 'slope')) overlays.push(SwisstopoMapProvider.slope30);
    if (isMapLayerEnabled(preferences, 'hikingRoutes')) overlays.push(SwisstopoMapProvider.hikingTrails);
    if (isMapLayerEnabled(preferences, 'wildlifeZones')) {
      overlays.push(SwisstopoMapProvider.wildlifeRestZones, SwisstopoMapProvider.wildlifeReserves);
    }
    return {
      baseLayer: base,
      rasterOverlays: overlays,
      usesFallbackBaseMap: false,
      attributions: SwisstopoMapProvider.attributions([base, ...overlays]),
    };
  }

  attributionsFor(layers: readonly TileLayerDescriptor[]): MapAttribution[] {
    return SwisstopoMapProvider.attributions(layers);
  }

  /**
   * Credits of the displayed layers, in order, each linked to its licensor; a credit whose text is already part of an
   * earlier one ("© swisstopo" after "© swisstopo, CNES, …") is skipped.
   */
  static attributions(layers: readonly TileLayerDescriptor[]): MapAttribution[] {
    const result: MapAttribution[] = [];
    for (const l of layers) {
      if (result.some((a) => a.text.includes(l.attribution))) continue;
      const url = l.id.startsWith('ch.bafu.') ? MapAttributions.bafu.url : MapAttributions.swisstopo.url;
      result.push({ text: l.attribution, url });
    }
    return result;
  }

  /**
   * `routeOnly`: none. Standard/detailed: topographic + hiking trails (zoom depth comes from the level). `allData`: adds
   * slope > 30° (useful for bivouac terrain checks offline). Only offline-capable layers.
   */
  offlineTileLayers(level: OfflinePackageLevel): TileLayerDescriptor[] {
    let layers: TileLayerDescriptor[];
    switch (level) {
      case 'routeOnly':
        layers = [];
        break;
      case 'standardMaps':
      case 'detailedMaps':
        layers = [SwisstopoMapProvider.topographic, SwisstopoMapProvider.hikingTrails];
        break;
      case 'allData':
        layers = [SwisstopoMapProvider.topographic, SwisstopoMapProvider.hikingTrails, SwisstopoMapProvider.slope30];
        break;
    }
    return layers.filter((l) => l.offlineCapable);
  }

  /** Regions of ≤ 40 km (stage boundaries when possible), ids "region:<routeID>:<index>:<level>". */
  downloadableRegions(route: TrailRoute, level: OfflinePackageLevel, now: EpochMs): OfflineRegion[] {
    return RouteRegionSplitter.regions(route, level, this.offlineTileLayers(level), now);
  }

  /** Local areas merged with remote wildlife rest zones (remote wins on equal ids). Never rejects for network errors. */
  async protectedAreas(bounds: GeoBounds): Promise<ProtectedArea[]> {
    const local = await this.localProtectedAreas(bounds);
    const query = SwisstopoMapProvider.remoteQueryBounds(bounds);
    if (!this.client || !(this.connectivity?.isOnline ?? true) || !query) return local;
    try {
      const remote = await GeoAdminWildlifeRestZones.fetch(query, this.client, this.now(), this.limiter);
      return SwisstopoMapProvider.merge(local, remote);
    } catch (error) {
      console.info('Wildlife rest zones unavailable, using local data', error);
      return local;
    }
  }

  // MARK: Internals

  /** `bounds` clipped to Switzerland, or undefined when outside or too large for a remote query. */
  static remoteQueryBounds(bounds: GeoBounds): GeoBounds | undefined {
    const swiss = switzerlandBounds;
    if (!boundsIntersect(bounds, swiss)) return undefined;
    const clipped: GeoBounds = {
      minLatitude: Math.max(bounds.minLatitude, swiss.minLatitude),
      minLongitude: Math.max(bounds.minLongitude, swiss.minLongitude),
      maxLatitude: Math.min(bounds.maxLatitude, swiss.maxLatitude),
      maxLongitude: Math.min(bounds.maxLongitude, swiss.maxLongitude),
    };
    const max = SwisstopoMapProvider.maximumRemoteQuerySpanDegrees;
    if (clipped.maxLatitude - clipped.minLatitude > max || clipped.maxLongitude - clipped.minLongitude > max) return undefined;
    return clipped;
  }

  /** Local order first (replaced by the remote version on equal ids), then new remote areas. */
  static merge(local: readonly ProtectedArea[], remote: readonly ProtectedArea[]): ProtectedArea[] {
    const remoteByID = new Map<string, ProtectedArea>();
    for (const area of remote) if (!remoteByID.has(area.id)) remoteByID.set(area.id, area);
    const result: ProtectedArea[] = [];
    const emitted = new Set<string>();
    for (const area of local) {
      if (emitted.has(area.id)) continue;
      emitted.add(area.id);
      result.push(remoteByID.get(area.id) ?? area);
    }
    for (const area of remote) {
      if (emitted.has(area.id)) continue;
      emitted.add(area.id);
      result.push(area);
    }
    return result;
  }
}

